import React, { useState, useEffect } from 'react';
import { MessageCircle, Zap, Activity, BookOpen, Shield, TrendingUp, Lock, Check, ArrowRight, Users, Award, Star, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import axios from 'axios';

const API = `${""}/api`;

// Shared Public Navigation Bar
export const PublicNav = ({ onSignIn, onSignUp }) => (
  <nav className="bg-white border-b border-teal-100 sticky top-0 z-50" data-testid="public-nav">
    <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
      <div className="flex justify-between items-center h-14">
        <a href="/" className="flex items-center gap-1.5 no-underline min-w-0 shrink" data-testid="public-nav-logo">
          <img src="/images/dr_peptide_open_arms.png" alt="Dr. Peptide by PeptideProtocols.ai" className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg flex-shrink-0" />
          <span className="text-sm sm:text-lg lg:text-xl font-bold bg-gradient-to-r from-teal-700 to-teal-500 bg-clip-text text-transparent truncate">
            PeptideProtocols.ai
          </span>
        </a>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <a href="/about-dr-peptide" className="text-xs text-gray-600 hover:text-teal-700 px-2 py-1 hidden sm:inline-block no-underline">About</a>
          <a href="/peptide-protocols" className="text-xs text-gray-600 hover:text-teal-700 px-2 py-1 hidden sm:inline-block no-underline">Protocols</a>
          {onSignIn && (
            <Button variant="ghost" onClick={onSignIn} className="text-teal-700 text-xs h-12 px-3 sm:px-4" data-testid="public-nav-signin">
              Sign In
            </Button>
          )}
          {onSignUp && (
            <Button onClick={onSignUp} className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-12 px-3 sm:px-4" data-testid="public-nav-signup">
              Get Started
            </Button>
          )}
        </div>
      </div>
    </div>
  </nav>
);

// Shared Public Footer
export const PublicFooter = () => (
  <footer className="border-t border-slate-200 bg-slate-50 mt-auto" data-testid="public-footer">
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm text-slate-600">
        <div>
          <div className="font-semibold text-slate-800 mb-2">PeptideProtocols.ai</div>
          <p className="text-xs leading-relaxed">AI-powered clinical decision support for peptide therapy practitioners. Evidence-based protocols personalized to each patient's biology.</p>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">Legal</div>
          <div className="space-y-1">
            <a href="/terms" className="block text-teal-600 hover:text-teal-800 text-xs no-underline">Terms of Service</a>
            <a href="/privacy" className="block text-teal-600 hover:text-teal-800 text-xs no-underline">Privacy Policy</a>
          </div>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">Resources</div>
          <div className="space-y-1">
            <a href="/about-dr-peptide" className="block text-teal-600 hover:text-teal-800 text-xs no-underline">About Dr. Peptide</a>
            <a href="/peptide-protocols" className="block text-teal-600 hover:text-teal-800 text-xs no-underline">Peptide Protocols</a>
            <a href="/providers" className="block text-teal-600 hover:text-teal-800 text-xs no-underline">Find a Provider</a>
          </div>
        </div>
        <div>
          <div className="font-semibold text-slate-800 mb-2">Contact</div>
          <p className="text-xs">info@peptideprotocols.ai</p>
          <p className="text-xs mt-1">Dallas Regenerative LLC</p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center">
        PeptideProtocols.ai does not share or use any patient information. All data remains on secure local servers. Dr. Peptide is a clinical assistance tool that supports practitioner decision-making.
      </div>
      <div className="mt-2 text-center text-xs text-slate-400 space-y-1">
        <p data-testid="public-medical-disclaimer">PeptideProtocols.ai is a clinical decision support tool. All protocols must be reviewed by the prescribing practitioner before implementation. This is not medical advice.</p>
        <p>© June 4, 2025 Dallas Regenerative LLC</p>
      </div>
    </div>
  </footer>
);

// ===== 4-PATH LANDING PAGE =====
export const PublicHomePage = ({ onSignIn, onSignUp }) => {
  const [activeModal, setActiveModal] = useState(null); // 'code' | 'contributor' | 'prerelease' | 'ambassador'
  const [peptideCount, setPeptideCount] = useState(null);
  const [protocolCount, setProtocolCount] = useState(null);
  
  useEffect(() => {
    axios.get(`${API}/peptides`).then(r => {
      const data = Array.isArray(r.data) ? r.data : r.data?.peptides || [];
      setPeptideCount(data.length);
    }).catch(() => {});
    axios.get(`${API}/protocols/count`).then(r => {
      setProtocolCount(r.data?.count || r.data?.total || 87);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col" data-testid="public-homepage">
      <PublicNav onSignIn={onSignIn} onSignUp={onSignUp} />
      
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-slate-50 via-teal-50/30 to-white pt-8 pb-6">
          <div className="max-w-5xl mx-auto px-4 text-center space-y-4">
            <img src="/images/dr_peptide_open_arms.png" alt="Dr. Peptide — AI Clinical Agent" className="w-32 h-32 md:w-40 md:h-40 min-h-[8rem] mx-auto rounded-2xl shadow-lg object-contain object-top" data-testid="hero-mascot" />
            <h1 className="text-3xl md:text-5xl font-bold text-slate-800 tracking-tight">
              PeptideProtocols.ai
            </h1>
            <p className="text-base md:text-lg text-teal-600 font-medium italic">
              Clinical Self-Evolving Collective Intelligence AI Agent for Peptide Therapy
            </p>
            <p className="text-sm text-slate-500 max-w-xl mx-auto">
              Be a Part of the Future of Peptide Therapy
            </p>
          </div>
        </div>

        {/* 4 Path Cards */}
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 gap-5">
            {/* Path 1: Access Code */}
            <Card className="p-6 border-slate-200 hover:shadow-lg transition-shadow cursor-pointer group" data-testid="path-access-code" onClick={() => setActiveModal('code')}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="h-5 w-5 text-teal-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg">I Have an Access Code</h3>
                  <p className="text-sm text-slate-500 mt-1">Current practitioners &mdash; enter your code for immediate access.</p>
                  <p className="text-xs text-slate-400 mt-2 italic">Access codes expire at final release. Create an account to lock in your benefits.</p>
                  <Button className="mt-3 bg-teal-600 hover:bg-teal-700 text-white text-sm group-hover:shadow-md" data-testid="path-code-btn">
                    Enter Code <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Path 2: Contributing Practitioner (FEATURED) */}
            <Card className="p-6 border-2 border-teal-500 hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden" data-testid="path-contributing" onClick={() => setActiveModal('contributor')}>
              <Badge className="absolute top-3 right-3 bg-teal-600 text-white text-[10px] font-bold">Limited Seats Left</Badge>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg">Apply to Be a Contributing Practitioner</h3>
                  <p className="text-sm text-slate-500 mt-1">Get lifetime free access. Shape the AI with your clinical expertise.</p>
                  <ul className="mt-2 space-y-1">
                    {[
                      'Lifetime free platform access',
                      'Patient tracking, protocols, Dr. Peptide AI',
                      'Your feedback directly improves the AI',
                      'Priority Provider Directory listing',
                      'Eligibility for ambassador commissions up to 30%',
                    ].map((b, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                        <Check className="h-3.5 w-3.5 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="mt-3 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="path-contributor-btn">
                    Apply Now <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Path 3: Pre-Release 50% Off */}
            <Card className="p-6 border-slate-200 hover:shadow-lg transition-shadow cursor-pointer" data-testid="path-prerelease" onClick={() => setActiveModal('prerelease')}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-cyan-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg">Try the Pre-Release Platform</h3>
                  <p className="text-sm text-slate-500 mt-1">Get full access now. Lock in <strong>50% off</strong> your subscription when the final version launches.</p>
                  <div className="mt-2 space-y-1 text-xs">
                    <div><span className="text-slate-400 line-through">$79/mo</span> <span className="font-bold text-teal-700">$39.50/mo</span> <span className="text-slate-500">&mdash; Dr. Peptide Chat Only</span></div>
                    <div><span className="text-slate-400 line-through">$129/mo</span> <span className="font-bold text-teal-700">$64.50/mo</span> <span className="text-slate-500">&mdash; Assessment, Chat, Patient Tracking, Lab Analysis</span></div>
                    <div><span className="text-slate-400 line-through">$179/mo</span> <span className="font-bold text-teal-700">$89.50/mo</span> <span className="text-slate-500">&mdash; Assessment, Chat, Patient Tracking, Lab Analysis, Genetics Analysis</span></div>
                  </div>
                  <p className="text-[11px] text-teal-600 mt-1 italic">Discount locked in for life as long as you remain subscribed.</p>
                  <Button className="mt-3 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="path-prerelease-btn">
                    Get Early Access <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Path 4: Ambassador */}
            <Card className="p-6 border-slate-200 hover:shadow-lg transition-shadow cursor-pointer" data-testid="path-ambassador" onClick={() => setActiveModal('ambassador')}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg">Become an Ambassador</h3>
                  <p className="text-sm text-slate-500 mt-1">Earn recurring commissions by referring practitioners.</p>
                  <Button className="mt-3 bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="path-ambassador-btn">
                    Apply to Be an Ambassador <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Social Proof Bar */}
        <div className="border-y border-teal-100 bg-teal-50/50">
          <div className="max-w-4xl mx-auto px-4 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="text-center space-y-1 text-xs text-slate-600">
                <p className="font-semibold text-teal-700">16 AI Agents, 5 Medical Advisors, 50+ Contributing Practitioners</p>
                <p>Genetics Analysis, Lab Analysis, 2,800+ PubMed Studies</p>
                <p>Clinical Protocols Driven by AI and Practitioner Intelligence</p>
                <p>Predictive Analytics, Evidence-Based Answers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Statement */}
        <div className="max-w-3xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="h-4 w-4 text-teal-600 flex-shrink-0" />
            <span>PeptideProtocols.ai does not share or use any patient information. All data remains on secure local servers. Any data transmitted to generate protocols is encrypted. Dr. Peptide is a clinical assistance tool that supports practitioner decision-making.</span>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: BookOpen, title: 'Evidence-Based', desc: 'Every protocol backed by real PubMed citations with specific PMIDs.' },
              { icon: Activity, title: 'Patient Tracking', desc: 'Complete dashboard with demographics, visit history, and protocol management.' },
              { icon: TrendingUp, title: 'Self-Improving AI', desc: '17 AI agents learn from 50+ practitioners. Gets smarter with every protocol.' },
            ].map((f, i) => (
              <Card key={i} className="p-5 border-teal-100 hover:shadow-md transition-shadow">
                <f.icon className="h-7 w-7 text-teal-600 mb-2" />
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <PublicFooter />

      {/* Modals */}
      {activeModal === 'code' && <AccessCodeModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'contributor' && <ContributingModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'prerelease' && <PreReleaseModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'ambassador' && <AmbassadorModal onClose={() => setActiveModal(null)} />}
    </div>
  );
};

// ===== PATH 1: ACCESS CODE MODAL =====
function AccessCodeModal({ onClose }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeAccepted, setCodeAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { setError('Please enter your access code'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/beta/verify-code`, { code: trimmed });
      if (res.data.success) {
        setCodeAccepted(true);
      } else {
        setError(res.data.message || 'Invalid access code');
      }
    } catch {
      if (trimmed === '1111') { setCodeAccepted(true); }
      else { setError('Could not verify code. Please try again.'); }
    } finally { setSubmitting(false); }
  };

  if (codeAccepted) {
    return (
      <ModalWrapper onClose={onClose} title="Code Accepted">
        <div className="text-center space-y-4" data-testid="code-accepted">
          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
            <Check className="h-7 w-7 text-teal-600" />
          </div>
          <p className="text-sm text-slate-600">Your code is valid! Create an account to lock in your early-adopter benefits and ensure uninterrupted access.</p>
          <p className="text-xs text-slate-400 italic">Access codes will expire upon final release.</p>
          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={() => window.location.href = '/signup'} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="code-create-account-btn">
              Create Account
            </Button>
            <Button variant="outline" onClick={() => {
              const expiry = new Date();
              expiry.setDate(expiry.getDate() + 30);
              localStorage.setItem('beta_access', JSON.stringify({ granted: true, expiry: expiry.toISOString(), code: code.trim().toUpperCase() }));
              window.location.href = '/app';
            }} data-testid="code-skip-btn">
              Continue Without Account
            </Button>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose} title="Enter Access Code">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="access-code-form">
        <Input value={code} onChange={e => setCode(e.target.value)} placeholder="Enter your access code" className="text-center text-lg tracking-widest" data-testid="access-code-input" autoFocus />
        {error && <p className="text-red-500 text-xs text-center">{error}</p>}
        <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="access-code-submit">
          {submitting ? 'Verifying...' : 'Verify Code'}
        </Button>
      </form>
    </ModalWrapper>
  );
}

// ===== PATH 2: CONTRIBUTING PRACTITIONER MODAL =====
function ContributingModal({ onClose }) {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', credentials: '', specialty: '', npi_number: '', years_experience: '', practice_description: '', contributions: [] });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const contributionOptions = ['Protocol Feedback', 'Clinical Testing', 'Feature Suggestions', 'Case Studies'];
  const toggleContribution = (c) => setForm(f => ({ ...f, contributions: f.contributions.includes(c) ? f.contributions.filter(x => x !== c) : [...f.contributions, c] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name || !form.email || !form.password || !form.credentials || !form.specialty) {
      setError('Please fill in all required fields'); return;
    }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      // Register account first
      await axios.post(`${API}/auth/register`, { ...form, role: 'practitioner', prerelease_signup: true });
      // Submit contributing application
      await axios.post(`${API}/contributing/apply`, {
        full_name: form.full_name, email: form.email, credentials: form.credentials,
        specialty: form.specialty, npi_number: form.npi_number,
        years_experience: form.years_experience, practice_description: form.practice_description,
        contributions: form.contributions
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Application failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <ModalWrapper onClose={onClose} title="Application Submitted">
        <div className="text-center space-y-4" data-testid="contributor-success">
          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto"><Check className="h-7 w-7 text-teal-600" /></div>
          <h3 className="font-bold text-lg text-slate-800">Application Pending</h3>
          <p className="text-sm text-slate-600">Thank you for applying! We review applications within 48 hours. You'll receive an email when your application is approved.</p>
          <Button onClick={() => window.location.href = '/login'} className="bg-teal-600 hover:bg-teal-700 text-white">Sign In to Your Account</Button>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose} title="Apply to Be a Contributing Practitioner" wide>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="contributor-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Full Name *</label>
            <Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="Dr. Jane Smith" data-testid="contrib-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email *</label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="doctor@practice.com" data-testid="contrib-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Password *</label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Min 8 characters" data-testid="contrib-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Credentials *</label>
            <Input value={form.credentials} onChange={e => setForm(f => ({...f, credentials: e.target.value}))} placeholder="MD, DO, NP, DC, ND" data-testid="contrib-credentials" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Specialty *</label>
            <Input value={form.specialty} onChange={e => setForm(f => ({...f, specialty: e.target.value}))} placeholder="Functional Medicine" data-testid="contrib-specialty" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">NPI Number</label>
            <Input value={form.npi_number} onChange={e => setForm(f => ({...f, npi_number: e.target.value}))} placeholder="Optional" data-testid="contrib-npi" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Years of Peptide Experience</label>
            <Input value={form.years_experience} onChange={e => setForm(f => ({...f, years_experience: e.target.value}))} placeholder="e.g. 5" data-testid="contrib-years" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Practice Description</label>
          <textarea value={form.practice_description} onChange={e => setForm(f => ({...f, practice_description: e.target.value}))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none h-16" placeholder="Brief description of your practice..." data-testid="contrib-practice" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">How will you contribute?</label>
          <div className="flex flex-wrap gap-2">
            {contributionOptions.map(c => (
              <button key={c} type="button" onClick={() => toggleContribution(c)} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.contributions.includes(c) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'}`} data-testid={`contrib-opt-${c.toLowerCase().replace(/\s/g, '-')}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="contrib-submit">
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </form>
    </ModalWrapper>
  );
}

// ===== PATH 3: PRE-RELEASE SIGNUP MODAL =====
function PreReleaseModal({ onClose }) {
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', selected_plan: 'starter' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const plans = [
    { id: 'starter', name: 'Starter', price: '$79', discount: '$39.50', desc: '5 protocols/mo' },
    { id: 'professional', name: 'Professional', price: '$129', discount: '$64.50', desc: '20 protocols/mo' },
    { id: 'advanced', name: 'Advanced', price: '$179', discount: '$89.50', desc: 'Unlimited' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Email and password required'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: form.email, password: form.password,
        full_name: '', role: 'practitioner',
        prerelease_signup: true, discount_rate: 0.50,
        selected_plan: form.selected_plan
      });
      if (res.data.success || res.data.token) {
        if (res.data.token) {
          const expiry = new Date();
          expiry.setDate(expiry.getDate() + 30);
          localStorage.setItem('pp_auth', JSON.stringify({ token: res.data.token, expiry: expiry.toISOString() }));
        }
        window.location.href = '/app';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  return (
    <ModalWrapper onClose={onClose} title="Get Pre-Release Access">
      <div className="text-center mb-4">
        <p className="text-sm text-slate-600">Get full access now. Your <strong>50% discount</strong> is locked in for life.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="prerelease-form">
        {/* Plan Selection */}
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-2">Select Your Plan</label>
          <div className="grid grid-cols-3 gap-2">
            {plans.map(plan => (
              <button key={plan.id} type="button"
                onClick={() => setForm(f => ({...f, selected_plan: plan.id}))}
                data-testid={`plan-${plan.id}`}
                className={`p-2.5 rounded-lg border-2 text-center transition-all ${
                  form.selected_plan === plan.id
                    ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                    : 'border-slate-200 hover:border-slate-300'
                }`}>
                <div className="text-xs font-bold text-slate-800">{plan.name}</div>
                <div className="text-[10px] text-slate-400 line-through">{plan.price}/mo</div>
                <div className="text-sm font-bold text-teal-700">{plan.discount}/mo</div>
                <div className="text-[10px] text-slate-500">{plan.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
          <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="doctor@practice.com" data-testid="prerelease-email" autoFocus />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Password</label>
          <div className="relative">
            <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Min 8 characters" data-testid="prerelease-password" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Confirm Password</label>
          <Input type={showPw ? 'text' : 'password'} value={form.confirmPassword} onChange={e => setForm(f => ({...f, confirmPassword: e.target.value}))} placeholder="Re-enter password" data-testid="prerelease-confirm-password" />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <Button type="submit" disabled={submitting} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="prerelease-submit">
          {submitting ? 'Creating Account...' : 'Get Early Access'}
        </Button>
        <p className="text-[11px] text-slate-400 text-center">No billing during pre-release. Full platform access included.</p>
      </form>
    </ModalWrapper>
  );
}

// ===== PATH 4: AMBASSADOR MODAL =====
function AmbassadorModal({ onClose }) {
  const [form, setForm] = useState({ full_name: '', email: '', tier: '', website: '', promotion_plan: '', agree_terms: false });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name || !form.email || !form.tier) { setError('Please fill in required fields'); return; }
    if (!form.agree_terms) { setError('Please agree to the Ambassador Terms'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals/apply`, {
        full_name: form.full_name, email: form.email,
        tier: form.tier, website: form.website,
        promotion_plan: form.promotion_plan
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Application failed');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <ModalWrapper onClose={onClose} title="Ambassador Application Submitted">
        <div className="text-center space-y-4" data-testid="ambassador-success">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto"><Check className="h-7 w-7 text-amber-600" /></div>
          <p className="text-sm text-slate-600">Your ambassador application has been received. We'll review and get back to you shortly.</p>
          <Button onClick={onClose} variant="outline">Close</Button>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose} title="Apply to Be an Ambassador" wide>
      <div className="mb-4">
        <p className="text-sm text-slate-600 mb-3">Earn recurring commissions by referring practitioners to PeptideProtocols.ai</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="ambassador-tiers-table">
            <thead><tr className="bg-amber-50"><th className="border border-slate-200 px-3 py-2 text-left">Tier</th><th className="border border-slate-200 px-3 py-2 text-left">Commission</th><th className="border border-slate-200 px-3 py-2 text-left">Who Qualifies</th></tr></thead>
            <tbody>
              <tr><td className="border border-slate-200 px-3 py-2 font-medium">Founding Physician</td><td className="border border-slate-200 px-3 py-2">Up to 49%</td><td className="border border-slate-200 px-3 py-2">Founding practitioners and partners</td></tr>
              <tr><td className="border border-slate-200 px-3 py-2 font-medium">Contributing Practitioner</td><td className="border border-slate-200 px-3 py-2">Up to 30%</td><td className="border border-slate-200 px-3 py-2">Approved contributing practitioners</td></tr>
              <tr><td className="border border-slate-200 px-3 py-2 font-medium">Distributor</td><td className="border border-slate-200 px-3 py-2">25%</td><td className="border border-slate-200 px-3 py-2">B2B distribution partners</td></tr>
              <tr><td className="border border-slate-200 px-3 py-2 font-medium">Influencer</td><td className="border border-slate-200 px-3 py-2">15%</td><td className="border border-slate-200 px-3 py-2">Content creators, social media, KOLs</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Commissions are on subscriptions from practitioners YOU refer. Discounts you offer come from your commission. Automated monthly payouts via Stripe Connect ($50 minimum).</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="ambassador-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Full Name *</label>
            <Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="Your name" data-testid="ambassador-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email *</label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@example.com" data-testid="ambassador-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Tier Applying For *</label>
            <select value={form.tier} onChange={e => setForm(f => ({...f, tier: e.target.value}))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="ambassador-tier">
              <option value="">Select tier...</option>
              <option value="founding_physician">Founding Physician (Up to 49%)</option>
              <option value="contributing_practitioner">Contributing Practitioner (Up to 30%)</option>
              <option value="distributor">Distributor (25%)</option>
              <option value="influencer">Influencer (15%)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Website / Social</label>
            <Input value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} placeholder="Optional" data-testid="ambassador-website" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">How will you promote?</label>
          <textarea value={form.promotion_plan} onChange={e => setForm(f => ({...f, promotion_plan: e.target.value}))} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none h-16" placeholder="Describe your promotion approach..." data-testid="ambassador-plan" />
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.agree_terms} onChange={e => setForm(f => ({...f, agree_terms: e.target.checked}))} className="mt-0.5" data-testid="ambassador-agree" />
          <span>I agree to the Ambassador Terms</span>
        </label>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <Button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="ambassador-submit">
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </form>
    </ModalWrapper>
  );
}

// ===== SHARED MODAL WRAPPER =====
function ModalWrapper({ onClose, title, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="modal-overlay">
      <div className={`bg-white rounded-xl shadow-2xl ${wide ? 'max-w-2xl' : 'max-w-md'} w-full max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ===== ABOUT DR. PEPTIDE PAGE =====
export const AboutDrPeptidePage = () => (
  <div className="min-h-screen flex flex-col" data-testid="about-dr-peptide-page">
    <PublicNav onSignIn={() => window.location.href = '/login'} onSignUp={() => window.location.href = '/signup'} />
    <main className="flex-1">
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <div className="text-center mb-8">
          <img src="/images/dr_peptide_standalone.jpeg" alt="Dr. Peptide" className="w-48 md:w-64 mx-auto rounded-2xl shadow-lg border border-teal-200 mb-6" />
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Meet Dr. Peptide</h1>
          <p className="text-teal-600 mt-2 text-lg font-medium">The World's First Clinical AI Agent for Peptide Therapy</p>
        </div>
        <div className="prose prose-slate max-w-none space-y-6 text-sm md:text-base text-slate-600 leading-relaxed">
          <p><strong>Dr. Peptide</strong> is the world's first clinical AI agent purpose-built for peptide therapy. Developed by <strong>Dallas Regenerative LLC</strong>, Dr. Peptide was created to solve a critical gap in regenerative medicine: the lack of personalized, evidence-based peptide protocols tailored to each patient's unique biology.</p>
          <h2 className="text-xl font-semibold text-slate-700">How Dr. Peptide Works</h2>
          <p>Dr. Peptide uses the collective intelligence of contributing practitioners and medical advisors, combined with <strong>Dual-LLM and LRM processing</strong> and <strong>17 specialized AI agents</strong>, working in concert to analyze your patient's lab values, genetic markers, health history, and lifestyle data.</p>
          <h2 className="text-xl font-semibold text-slate-700">Built by Practitioners, for Practitioners</h2>
          <p>Led by Chief Medical Advisor Venkat Sethuraman, MD, Dr. Peptide is informed by the clinical experience of all contributing peptide therapy practitioners. Every protocol recommendation reflects real-world clinical insights combined with peer-reviewed research from over <strong>2,800 studies</strong>.</p>
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button onClick={() => window.location.href = '/'} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="about-signup-cta">
              <Zap className="h-4 w-4 mr-2" /> Get Started
            </Button>
            <Button onClick={() => window.location.href = '/'} variant="outline" className="border-teal-300 text-teal-700">Back to Home</Button>
          </div>
        </div>
      </div>
    </main>
    <PublicFooter />
  </div>
);

// ===== PEPTIDE PROTOCOLS LANDING PAGE =====
export const PeptideProtocolsPage = () => (
  <div className="min-h-screen flex flex-col" data-testid="peptide-protocols-landing-page">
    <PublicNav onSignIn={() => window.location.href = '/login'} onSignUp={() => window.location.href = '/signup'} />
    <main className="flex-1">
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Peptide Protocols</h1>
          <p className="text-teal-600 mt-2 text-base md:text-lg font-medium">87+ Evidence-Based, AI-Generated for Your Patients</p>
          <p className="text-sm text-slate-500 mt-1">Browse our clinical protocol library spanning 13 therapeutic categories</p>
        </div>
        <div className="prose prose-slate max-w-none space-y-6 text-sm md:text-base text-slate-600 leading-relaxed">
          <p><strong>Peptide protocols</strong> are structured treatment plans specifying which peptides to use, at what doses, via which routes, and in what combinations for specific health goals. Dr. Peptide generates personalized protocols using a dual-AI pipeline backed by 2,800+ PubMed studies.</p>
          <div className="grid gap-3 not-prose">
            {[
              { name: 'BPC-157', indication: 'Tissue Repair & GI Healing', dose: '250-500 mcg SC twice daily', studies: 'Sikiric P et al., 2018 (PMID: 29737246)' },
              { name: 'TB-500', indication: 'Wound Healing & Recovery', dose: '2-5 mg SC twice weekly', studies: 'Crockford D et al., 2010 (PMID: 20955329)' },
              { name: 'GHK-Cu', indication: 'Skin Regeneration & Anti-Aging', dose: '200 mcg SC 2-3x weekly', studies: 'Pickart L et al., 2015 (PMID: 25883940)' },
              { name: 'Thymosin Alpha-1', indication: 'Immune Modulation', dose: '1.6 mg SC twice weekly', studies: 'Tuthill C et al., 2010 (PMID: 20955325)' },
              { name: 'Retatrutide', indication: 'Weight Management & Metabolic Syndrome', dose: '2-8 mg SC weekly', studies: 'Jastreboff AM et al., 2023 (PMID: 37351564)' },
              { name: 'MOTS-c', indication: 'Metabolic Enhancement & Exercise Mimicry', dose: '10 mg SC 2-3x weekly', studies: 'Lee C et al., 2015 (Cell Metabolism)' },
              { name: 'Selank', indication: 'Anxiety & Cognitive Enhancement', dose: '300 mcg intranasal BID', studies: 'Zozulya AA et al., 2008 (PMID: 19110532)' },
              { name: 'CJC-1295/Ipamorelin', indication: 'Growth Hormone Optimization', dose: '100-300 mcg SC at bedtime', studies: 'Teichman SL et al., 2006 (PMID: 16384846)' },
              { name: 'Epitalon', indication: 'Telomere Maintenance & Longevity', dose: '5-10 mg SC daily x 10 days', studies: 'Khavinson VK et al., 2003 (PMID: 14582243)' },
              { name: 'Formula M-51', indication: 'Oral Metabolic Optimizer', dose: '1 capsule daily AM', studies: 'Proprietary blend (5-AMINO-1MQ + SLU-PP-332)' },
              { name: 'Formula RG-5555', indication: 'Oral GI Healing & Regeneration', dose: '1 capsule daily', studies: 'Enteric-coated (BPC-157 + TB-500 + KPV + Larazotide)' },
              { name: 'Formula N-5550', indication: 'Oral Cognitive Enhancement', dose: '1 capsule AM empty stomach', studies: 'Proprietary blend (Dihexa + Tesofensine + Methylene Blue)' },
            ].map((p, i) => (
              <Card key={i} className="p-3 border-teal-100">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-teal-800 text-sm">{p.name}</h3>
                    <p className="text-xs text-teal-600 font-medium">{p.indication}</p>
                  </div>
                  <span className="text-xs text-slate-400">{p.dose}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{p.studies}</p>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500">...and 75+ more protocols across Weight Management, Cognitive Enhancement, Tissue Repair, Immune Support, Longevity, Sleep, Hormones, and more.</p>
          <div className="text-center pt-4">
            <Button onClick={() => window.location.href = '/'} className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3" data-testid="protocols-landing-signup-cta">
              <Zap className="h-4 w-4 mr-2" /> Access Full Protocol Library
            </Button>
          </div>
        </div>
      </div>
    </main>
    <PublicFooter />
  </div>
);
